// The four Customize tabs: Rules (modules by category, with dependency
// handling), World (tuning), Players (AI seats + the human's assists) and
// Content (rating). Every edit goes through the pure helpers in setupModel.

import {
  type AiProfile,
  type Assists,
  type ContentRating,
  MODULE_IDS,
  type ModuleCategory,
  type ModuleId,
  type Tuning,
  moduleCategory,
} from '@aod/protocol';
import { cn } from '@aod/ui/lib/utils';
import { CircleAlert, Heart, Link2, Lock, Shield, ShieldAlert, Undo2 } from 'lucide-react';
import { type KeyboardEvent, useId, useRef } from 'react';
import { type MessageKey, type Translate, useLocale, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { SegmentedRow, StepperRow, Switch, SwitchRow, nextRovingIndex } from './controls.js';
import {
  categoryLabel,
  formatList,
  localizeProblem,
  moduleDescription,
  moduleLabel,
} from './presentation.js';
import {
  type SetupDraft,
  enabledDependents,
  isModuleLocked,
  missingDependencies,
  setAiProfile,
  setAlignment,
  setAssist,
  setContent,
  setModule,
  setTuning,
} from './setupModel.js';

export type SetupTab = 'rules' | 'world' | 'players' | 'content';
export const SETUP_TABS: readonly SetupTab[] = ['rules', 'world', 'players', 'content'];

export interface Notice {
  readonly text: string;
  /** Draft to go back to with Undo. */
  readonly undo?: SetupDraft;
}

export interface TabProps {
  readonly draft: SetupDraft;
  readonly onChange: (next: SetupDraft) => void;
}
// ── Tab list ───────────────────────────────────────────────────────────

interface TabListProps {
  readonly tab: SetupTab;
  readonly onTab: (tab: SetupTab) => void;
  readonly idBase: string;
}

export function SetupTabList({ tab, onTab, idBase }: TabListProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const next = nextRovingIndex(e.key, SETUP_TABS.indexOf(tab), SETUP_TABS.length);
    if (next === null) return;
    e.preventDefault();
    const nextTab = SETUP_TABS[next];
    if (!nextTab) return;
    onTab(nextTab);
    ref.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };
  const tabButton = (id: SetupTab) =>
    cn(
      'min-h-10 flex-1 cursor-pointer rounded-md px-3 text-sm font-semibold transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      tab === id
        ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
        : 'text-muted-foreground hover:text-foreground',
    );
  const common = (id: SetupTab) => ({
    type: 'button' as const,
    role: 'tab' as const,
    id: `${idBase}-tab-${id}`,
    'aria-selected': tab === id,
    'aria-controls': `${idBase}-panel`,
    tabIndex: tab === id ? 0 : -1,
    onClick: () => onTab(id),
    className: tabButton(id),
  });
  // Literal testids (one per tab) so testid-audit can see them.
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={t('setup.tabs')}
      onKeyDown={onKeyDown}
      className="flex gap-1 rounded-lg border border-border bg-muted/50 p-1"
    >
      <button
        {...common('rules')}
        data-testid="setup-tab-rules"
        {...appDom({ id: 'setup-tab-rules', type: 'Tabs', role: 'button', scope: 'main' })}
      >
        {t('setup.tab.rules')}
      </button>
      <button
        {...common('world')}
        data-testid="setup-tab-world"
        {...appDom({ id: 'setup-tab-world', type: 'Tabs', role: 'button', scope: 'main' })}
      >
        {t('setup.tab.world')}
      </button>
      <button
        {...common('players')}
        data-testid="setup-tab-players"
        {...appDom({ id: 'setup-tab-players', type: 'Tabs', role: 'button', scope: 'main' })}
      >
        {t('setup.tab.players')}
      </button>
      <button
        {...common('content')}
        data-testid="setup-tab-content"
        {...appDom({ id: 'setup-tab-content', type: 'Tabs', role: 'button', scope: 'main' })}
      >
        {t('setup.tab.content')}
      </button>
    </div>
  );
}
export { RulesTab } from './tabs/RulesTab.js';
export { WorldTab } from './tabs/WorldTab.js';
export { PlayersTab } from './tabs/PlayersTab.js';
export { ContentTab } from './tabs/ContentTab.js';
